import { Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';

import useApiQuery from 'lib/api/useApiQuery';
import { Heading } from 'toolkit/chakra/heading';
import { Skeleton } from 'toolkit/chakra/skeleton';
import TokenEntity from 'ui/shared/entities/token/TokenEntity';
import { isBlacklistedToken } from 'ui/tokens/utils';

const MAX_ITEMS = 10;

// Live prices for ERC-20 tokens deployed on Xhavic, sourced from the same
// Blockscout token list API as the /tokens page (real on-chain data - price is
// only shown when the backend actually has a rate for that token; otherwise the
// token still appears, just without a fabricated price).
const TokenTicker = () => {
  const { data, isPlaceholderData, isPending, isError } = useApiQuery('general:tokens', {
    queryParams: { sort: 'fiat_value', order: 'desc' },
    queryOptions: {
      refetchInterval: 60_000,
    },
  });

  const items = React.useMemo(() => {
    return (data?.items ?? []).filter((token) => !isBlacklistedToken(token)).slice(0, MAX_ITEMS);
  }, [ data?.items ]);

  if (isError || (!isPending && items.length === 0)) {
    return null;
  }

  return (
    <Box mb={ 6 }>
      <Heading level="3" mb={ 3 }>Tokens</Heading>
      <Flex overflowX="auto" gap={ 3 } pb={ 1 }>
        { isPending || isPlaceholderData ? (
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={ index } loading height="52px" minW="160px" borderRadius="md" flexShrink={ 0 }/>
          ))
        ) : (
          items.map((token) => (
            <Flex
              key={ token.address_hash }
              alignItems="center"
              gap={ 2 }
              px={ 3 }
              py={ 2 }
              flexShrink={ 0 }
              borderWidth="1px"
              borderColor="border.divider"
              borderRadius="md"
            >
              <TokenEntity token={ token } isLoading={ false } onlySymbol noCopy/>
              <Text fontWeight="600" whiteSpace="nowrap" color={ token.exchange_rate ? undefined : 'text.secondary' }>
                { token.exchange_rate ?
                  `$${ Number(token.exchange_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }) }` :
                  'no price data' }
              </Text>
            </Flex>
          ))
        ) }
      </Flex>
    </Box>
  );
};

export default TokenTicker;
